/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import TrackingAssetCategoryFilter, {
  TrackingAssetCategoryFilterHandle
} from './TrackingAssetCategoryFilter';
import TrackingAssetStatusFilter, {
  TrackingAssetStatusFilterHandle
} from './TrackingAssetStatusFilter';
import TrackingAssetTypeFilter, {TrackingAssetTypeFilterHandle} from './TrackingAssetTypeFilter';
import TrackingGeofenceFilter, {TrackingGeofenceFilterHandle} from './TrackingGeofenceFilter';
import TrackingGroupsAssetsFilter, {
  TrackingGroupsAssetsFilterHandle
} from './TrackingGroupsAssetsFilter';
import TrackingRouteFilter, {TrackingRouteFilterHandle} from './TrackingRouteFilter';
import {TrackingDrawerFilterHandleBase, TrackingFilterNavigationRequest} from './types';

interface TrackingFilterDrawerCategoryOptions {
  onRequestNavigate?: TrackingFilterNavigationRequest;
  onSelectionChange: () => void;
}

interface TrackingFilterDrawerCategoryCounts {
  assetCategory: number;
  assetStatus: number;
  assetType: number;
  geofence: number;
  groupsAssets: number;
  route: number;
}

interface TrackingFilterDrawerCategoryContent {
  assetCategory: ReactNode;
  assetStatus: ReactNode;
  assetType: ReactNode;
  geofence: ReactNode;
  groupsAssets: ReactNode;
  route: ReactNode;
}

interface TrackingFilterDrawerCategories {
  counts: TrackingFilterDrawerCategoryCounts;
  filters: TrackingFilterDrawerCategoryContent;
  getFilterHandles: () => TrackingDrawerFilterHandleBase[];
  selectedFiltersCount: number;
}

/**
 * Builds tracking drawer filter category content, refs, and selected counts.
 *
 * @param options Defines selection change handling for all category filters, and
 *                the guard their links away from Tracking are run through.
 * @returns Drawer filter content, selected counts, selected total, and active handles.
 */
export const useTrackingFilterDrawerCategories = ({
  onRequestNavigate,
  onSelectionChange
}: TrackingFilterDrawerCategoryOptions): TrackingFilterDrawerCategories => {
  const assetTypeFilterRef     = useRef<TrackingAssetTypeFilterHandle>(null);
  const assetCategoryFilterRef = useRef<TrackingAssetCategoryFilterHandle>(null);
  const groupsAssetsFilterRef  = useRef<TrackingGroupsAssetsFilterHandle>(null);
  const geofenceFilterRef      = useRef<TrackingGeofenceFilterHandle>(null);
  const routeFilterRef         = useRef<TrackingRouteFilterHandle>(null);
  const assetStatusFilterRef   = useRef<TrackingAssetStatusFilterHandle>(null);

  const [assetTypeSelectedCount, setAssetTypeSelectedCount]         = useState(0);
  const [assetCategorySelectedCount, setAssetCategorySelectedCount] = useState(0);
  const [groupsAssetsSelectedCount, setGroupsAssetsSelectedCount]   = useState(0);
  const [geofenceSelectedCount, setGeofenceSelectedCount]           = useState(0);
  const [routeSelectedCount, setRouteSelectedCount]                 = useState(0);
  const [assetStatusSelectedCount, setAssetStatusSelectedCount]     = useState(0);

  const selectedFiltersCount = (
    assetTypeSelectedCount +
    assetCategorySelectedCount +
    groupsAssetsSelectedCount +
    geofenceSelectedCount +
    routeSelectedCount +
    assetStatusSelectedCount
  );

  const getFilterHandles = useCallback(() => ([
    groupsAssetsFilterRef.current,
    assetTypeFilterRef.current,
    assetCategoryFilterRef.current,
    geofenceFilterRef.current,
    routeFilterRef.current,
    assetStatusFilterRef.current
  ].filter((handle): handle is TrackingDrawerFilterHandleBase => !!handle)), []);

  const filters = useMemo(() => ({
    assetType : (
      <TrackingAssetTypeFilter ref={assetTypeFilterRef}
        onRequestNavigate={onRequestNavigate}
        onSelectedCountChange={setAssetTypeSelectedCount}
        onSelectionChange={onSelectionChange} />
    ),
    groupsAssets : (
      <TrackingGroupsAssetsFilter ref={groupsAssetsFilterRef}
        onRequestNavigate={onRequestNavigate}
        onSelectedCountChange={setGroupsAssetsSelectedCount}
        onSelectionChange={onSelectionChange} />
    ),
    assetCategory : (
      <TrackingAssetCategoryFilter ref={assetCategoryFilterRef}
        onRequestNavigate={onRequestNavigate}
        onSelectedCountChange={setAssetCategorySelectedCount}
        onSelectionChange={onSelectionChange} />
    ),
    geofence : (
      <TrackingGeofenceFilter ref={geofenceFilterRef}
        onRequestNavigate={onRequestNavigate}
        onSelectedCountChange={setGeofenceSelectedCount}
        onSelectionChange={onSelectionChange} />
    ),
    route : (
      <TrackingRouteFilter ref={routeFilterRef}
        onRequestNavigate={onRequestNavigate}
        onSelectedCountChange={setRouteSelectedCount}
        onSelectionChange={onSelectionChange} />
    ),
    assetStatus : (
      <TrackingAssetStatusFilter ref={assetStatusFilterRef}
        onSelectedCountChange={setAssetStatusSelectedCount}
        onSelectionChange={onSelectionChange} />
    )
  }), [onRequestNavigate, onSelectionChange]);

  return {
    counts : {
      assetCategory : assetCategorySelectedCount,
      assetStatus   : assetStatusSelectedCount,
      assetType     : assetTypeSelectedCount,
      geofence      : geofenceSelectedCount,
      groupsAssets  : groupsAssetsSelectedCount,
      route         : routeSelectedCount
    },
    filters,
    getFilterHandles,
    selectedFiltersCount
  };
};

export default useTrackingFilterDrawerCategories;
